// noinspection JSUnusedLocalSymbols

import type {
  ForwardedRef,
  FunctionComponent,
  JSX,
  ReactElement,
  Ref,
} from 'react';
import React, { forwardRef } from 'react';
import styled from 'styled-components';
import type {
  BackgroundColorProps,
  BackgroundProps,
  BorderProps,
  ColorProps,
  FlexboxProps,
  LayoutProps,
  OpacityProps,
  PositionProps,
  SpaceProps,
  TypographyProps,
} from 'styled-system';
import {
  border,
  color,
  flexbox,
  layout,
  position,
  space,
  typography,
} from 'styled-system';

export interface BoxProps
  extends SpaceProps,
    LayoutProps,
    FlexboxProps,
    BackgroundProps,
    BackgroundColorProps,
    BorderProps,
    ColorProps,
    PositionProps,
    OpacityProps,
    TypographyProps {}

type Tags =
  | 'div'
  | 'select'
  | 'textarea'
  | 'label'
  | 'tr'
  | 'th'
  | 'td'
  | 'section'
  | 'input'
  | 'ul';

// https://www.typescriptlang.org/docs/handbook/2/conditional-types.html#distributive-conditional-types

type Dom<Tag extends Tags | undefined> = Tag extends Tags
  ? JSX.IntrinsicElements[Tag]
  : JSX.IntrinsicElements['div'];

/** Ensures that the props do not include string ref, which cannot be forwarded */
type PropsWithRef<Props extends Dom<Tags>> =
  Props extends Dom<Tags>
    ? Props & {
        ref?: Exclude<Props['ref'], string>;
      }
    : never;

type DomProps<Tag extends Tags | undefined> = Tag extends Tags | undefined
  ? PropsWithRef<Dom<Tag>>
  : never;

type DomRef<Tag extends Tags | undefined> = Tag extends Tags | undefined
  ? Exclude<Dom<Tag>['ref'], string | undefined> extends Ref<infer Instance>
    ? Instance
    : never
  : never;

type MakeAttrsOptional<Attrs extends DomProps<Tags>, Props extends object> =
  // Distribute unions early to avoid quadratic expansion
  Attrs extends DomProps<Tags> ? Partial<Attrs & Props> : never;

type StyledComponentProps<
  // The Tag from whose props are derived
  Tag extends Tags,
  // The other props added by the template
  Props extends object,
> =
  // Distribute Props if Props is a union type
  Props extends object
    ? MakeAttrsOptional<
        DomProps<Tag>,
        {
          as?: Tag;
        } & Props
      >
    : never;

interface StyledComponent<Tag extends Tags, Props extends object> {
  // add our own fake call signature to implement the polymorphic 'as' prop
  (
    props: StyledComponentProps<Tag, Props> & {
      as?: never;
    },
    ref?: ForwardedRef<DomRef<Tag>>,
  ): ReactElement<StyledComponentProps<Tag, Props>>;

  <As extends Tags>(
    props: StyledComponentProps<As, Props>,
    ref?: ForwardedRef<DomRef<As>>,
  ): ReactElement<StyledComponentProps<As, Props>>;
}

type _BoxType = StyledComponent<'div', BoxProps>;

type AllowedProps =
  | 'suppressHydrationWarning'
  | 'onContextMenu'
  | 'onMouseDown'
  | 'onMouseEnter'
  | 'placeholder'
  | 'onDragStart'
  | 'onDragEnter'
  | 'onDragLeave'
  | 'onDragOver'
  | 'onMouseLeave'
  | 'onMouseUp'
  | 'onDragEnd'
  | 'onKeyDown'
  | 'onTouchStart'
  | 'onTouchEnd'
  | 'onTouchMove'
  | 'draggable'
  | 'className'
  | 'maxLength'
  | 'children'
  | 'onChange'
  | 'disabled'
  | 'readOnly'
  | 'htmlFor'
  | 'checked'
  | 'colSpan'
  | 'onClick'
  | 'onBlur'
  | 'onDrop'
  | 'title'
  | 'style'
  | 'scope'
  | 'value'
  | 'type'
  | 'ref';

type OptionalAllowedProps<TagProps extends DomProps<Tags>> =
  // Distribute unions early to avoid quadratic expansion
  TagProps extends DomProps<Tags>
    ? {
        [Prop in AllowedProps]?: Prop extends keyof TagProps
          ? TagProps[Prop]
          : never;
      }
    : never;

type PolymorphicProps<As extends Tags | undefined> = As extends Tags | undefined
  ? {
      'data-testid'?: string;
    } & (As extends Tags
      ? {
          as: As;
        }
      : {
          as?: never;
        }) &
      OptionalAllowedProps<DomProps<As>>
  : never;

type AllProps = {
  [Prop in AllowedProps]?: {
    // Union of prop for all tags where prop is defined
    [Tag in Tags]: Prop extends keyof DomProps<Tag>
      ? DomProps<Tag>[Prop]
      : never;
  }[Tags];
};

// Accepts all allowed values, intersection with Tags allows cast without first casting to unknown
type AllRenderer = Tags & FunctionComponent<AllProps>;

function BoxRenderer(
  props: PolymorphicProps<undefined>,
  ref?: ForwardedRef<HTMLDivElement>,
): ReactElement<PolymorphicProps<undefined>, 'div'>;

function BoxRenderer<As extends Tags>(
  props: PolymorphicProps<As>,
  ref?: ForwardedRef<DomRef<As>>,
): ReactElement<PolymorphicProps<As>, As>;

function BoxRenderer<As extends Tags | undefined>(
  props: PolymorphicProps<As>,
  ref?: {
    [Tag in Tags]: ForwardedRef<DomRef<Tag>>;
  }[Tags],
): ReactElement<PolymorphicProps<As>, As extends Tags ? As : 'div'> {
  const {
    onContextMenu,
    onMouseDown,
    onMouseEnter,
    placeholder,
    onDragStart,
    onDragEnter,
    onDragLeave,
    onDragOver,
    onMouseLeave,
    onMouseUp,
    onDragEnd,
    onKeyDown,
    onTouchStart,
    onTouchEnd,
    onTouchMove,
    draggable,
    className,
    maxLength,
    children,
    onChange,
    disabled,
    htmlFor,
    checked,
    colSpan,
    onClick,
    onBlur,
    onDrop,
    title,
    style,
    scope,
    value,
    type,
  } = props as AllProps;
  const As = (props.as ?? 'div') as AllRenderer;
  return (
    <As
      suppressHydrationWarning
      data-testid={props['data-testid']}
      onContextMenu={onContextMenu}
      onMouseDown={onMouseDown}
      onMouseEnter={onMouseEnter}
      placeholder={placeholder}
      onDragStart={onDragStart}
      onDragEnter={onDragEnter}
      onDragLeave={onDragLeave}
      onDragOver={onDragOver}
      onMouseLeave={onMouseLeave}
      onMouseUp={onMouseUp}
      onDragEnd={onDragEnd}
      onKeyDown={onKeyDown}
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
      onTouchMove={onTouchMove}
      draggable={draggable}
      className={className}
      maxLength={maxLength}
      onChange={onChange}
      disabled={disabled}
      htmlFor={htmlFor}
      checked={checked}
      colSpan={colSpan}
      onClick={onClick}
      onBlur={onBlur}
      onDrop={onDrop}
      title={title}
      style={style}
      scope={scope}
      value={value}
      type={type}
      ref={ref}
    >
      {children}
    </As>
  );
}

interface Box {
  (props: BoxProps & PolymorphicProps<undefined>): ReactElement;
  <As extends Tags>(props: BoxProps & PolymorphicProps<As>): ReactElement;
}

// forwardRef forgets polymorphic signature, have to define interface explicitly
const Box: Box = styled(forwardRef(BoxRenderer)).attrs((props) => ({
  style: position(props),
}))<BoxProps>(
  {
    boxSizing: 'border-box',
    minWidth: 0,
  },
  border,
  color,
  flexbox,
  layout,
  space,
  typography,
);

export default Box;

// Type tests
if (process.env.APP_ENV === 'never') {
  type Expect<T extends true> = T;

  type NonExistingTags = Exclude<Tags, keyof JSX.IntrinsicElements>;

  type DoesEveryAllowedTagExistInDom = NonExistingTags extends never
    ? true
    : false;

  // Checks that every allowed tag exists in React DOM.
  type _CheckTags = Expect<DoesEveryAllowedTagExistInDom>;

  type DomKeys<Tag extends Tags> = Tag extends Tags // Distribute over Tags union
    ? keyof DomProps<Tag>
    : never;

  type PossibleProps = DomKeys<Tags>;

  type NonExistingProps = Exclude<AllowedProps, PossibleProps>;

  type DoesEveryAllowedPropExistInSomeAllowedDomElement =
    NonExistingProps extends never ? true : false;

  // Checks that every allowed prop exists on at least one allowed element.
  type _Check_props = Expect<DoesEveryAllowedPropExistInSomeAllowedDomElement>;

  type Equal<A, B extends A> = Expect<A extends B ? true : never>;

  // Test type inference
  const _Baseline = styled.div<BoxProps>``;
  const BoxTest = Box;
  const _DivTest = (
    // @ts-expect-no-error works without parameter types
    <BoxTest
      onContextMenu={(e) => {
        type _test = Equal<
          typeof e,
          React.MouseEvent<HTMLDivElement, MouseEvent>
        >;
        type _test2 = Equal<
          typeof e,
          // @ts-expect-error Should not allow casting to incorrect type
          React.MouseEvent<HTMLLabelElement, MouseEvent>
        >;
        e.preventDefault();
      }}
    />
  );
  const _DivTest2 = (
    // @ts-expect-no-error works with types
    <BoxTest
      onContextMenu={(e: React.MouseEvent<HTMLDivElement>) => {
        type _test = Equal<
          typeof e,
          React.MouseEvent<HTMLDivElement, MouseEvent>
        >;
        type _test2 = Equal<
          typeof e,
          // @ts-expect-error Should not allow casting to incorrect type
          React.MouseEvent<HTMLLabelElement, MouseEvent>
        >;
        e.preventDefault();
      }}
    />
  );
  const _DivTest3 = (
    // @ts-expect-error Should error when signature doesn't match exactly, works incorrectly in e.g. plain styled.div atm
    <BoxTest
      onContextMenu={(e: React.MouseEvent<HTMLLabelElement>) => {
        e.preventDefault();
      }}
    />
  );
  const _LabelTest = (
    // @ts-expect-no-error
    <BoxTest
      as="label"
      onContextMenu={(e) => {
        type _test = Equal<
          typeof e,
          // @ts-expect-no-error
          React.MouseEvent<HTMLLabelElement, MouseEvent>
        >;
        type _test2 = Equal<
          typeof e,
          // @ts-expect-error Should not allow casting to incorrect type
          React.MouseEvent<HTMLDivElement, MouseEvent>
        >;
        e.preventDefault();
      }}
    />
  );
  const _LabelTest2 = (
    // @ts-expect-no-error
    <BoxTest
      as="label"
      onContextMenu={(e: React.MouseEvent<HTMLLabelElement>) => {
        type _test = Equal<
          typeof e,
          // @ts-expect-no-error
          React.MouseEvent<HTMLLabelElement, MouseEvent>
        >;
        type _test2 = Equal<
          typeof e,
          // @ts-expect-error Should not allow casting to incorrect type
          React.MouseEvent<HTMLDivElement, MouseEvent>
        >;
        e.preventDefault();
      }}
    />
  );
  const _LabelTest3 = (
    // @ts-expect-error Should error when signature doesn't match exactly
    <BoxTest
      as="label"
      onContextMenu={(e: React.MouseEvent<HTMLDivElement>) => {
        e.preventDefault();
      }}
    />
  );
  const _ThTest = (
    // @ts-expect-no-error
    <BoxTest as="th" scope="row" fontWeight="normal" textAlign="left" p={2}>
      test
    </BoxTest>
  );
}
